import { Fragment, useState } from 'react';
import { Folder, FolderOpen, FolderPlus, Pencil, X, Check, Files, ImageIcon, ChevronRight, ChevronDown } from 'lucide-react';

/** Arborescence de dossiers libres pour les fichiers d'un projet (création, renommage, suppression, dépôt glisser-déposer). */
export const FileFoldersPanel = ({ folders, selected, onSelect, onCreate, onRename, onDelete, onDropFile, totalCount, refCount }) => {
  const [creatingIn, setCreatingIn] = useState(undefined); // undefined = fermé, null = racine, id = sous-dossier
  const [draft, setDraft] = useState('');
  const [editing, setEditing] = useState(null);
  const [over, setOver] = useState(null);
  const [collapsed, setCollapsed] = useState({});
  const children = (pid) => folders.filter((f) => (f.parent_id || null) === pid);

  const submitCreate = async () => {
    const name = draft.trim(); const parent = creatingIn;
    setCreatingIn(undefined); setDraft('');
    if (name) await onCreate(name, parent === null ? null : parent);
  };
  const submitRename = async (f) => {
    const name = draft.trim(); setEditing(null); setDraft('');
    if (name && name !== f.name) await onRename(f, name);
  };
  const drop = (key) => (e) => { e.preventDefault(); setOver(null); const id = e.dataTransfer.getData('text/file-id'); if (id) onDropFile(id, key === 'root' ? null : key); };
  const dragProps = (key) => ({ onDragOver: (e) => { e.preventDefault(); setOver(key); }, onDragLeave: () => setOver(null), onDrop: drop(key) });

  const Item = ({ k, icon: Icon, label, count, depth = 0, folder, droppable = true }) => (
    <div
      role="button" tabIndex={0}
      onClick={() => onSelect(k)}
      onKeyDown={(e) => e.key === 'Enter' && onSelect(k)}
      {...(droppable ? dragProps(k) : {})}
      data-testid={folder ? `file-folder-${folder.folder_id}` : `file-folder-${k}`}
      style={{ paddingLeft: 10 + depth * 14 }}
      className={`group flex items-center gap-2 pr-2 py-1.5 rounded-md text-[12.5px] cursor-pointer border border-transparent transition-colors
        ${selected === k ? 'bg-[var(--c-gold-soft)] text-[var(--c-text)]' : 'text-[var(--c-text-2)] hover:bg-[var(--c-surface-2)]'} ${over === k ? '!border-[var(--c-gold)] border-dashed' : ''}`}
    >
      {folder && children(folder.folder_id).length > 0 ? (
        <button type="button" onClick={(e) => { e.stopPropagation(); setCollapsed((c) => ({ ...c, [folder.folder_id]: !c[folder.folder_id] })); }} className="w-4 h-4 -ml-1 flex items-center justify-center text-[var(--c-text-faint)]">
          {collapsed[folder.folder_id] ? <ChevronRight className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>
      ) : folder ? <span className="w-3" /> : null}
      <Icon className="w-[15px] h-[15px] shrink-0" />
      {editing === folder?.folder_id ? (
        <input autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={() => submitRename(folder)} onKeyDown={(e) => { if (e.key === 'Enter') submitRename(folder); if (e.key === 'Escape') setEditing(null); }} onClick={(e) => e.stopPropagation()} data-testid={`file-folder-rename-input-${folder.folder_id}`} className="flex-1 min-w-0 bg-transparent border-b border-[var(--c-gold)] outline-none text-[12.5px]" />
      ) : (
        <>
          <span className="flex-1 truncate">{label}</span>
          {folder && (
            <span className="hidden group-hover:flex items-center gap-0.5">
              <button type="button" title="Sous-dossier" onClick={(e) => { e.stopPropagation(); setCreatingIn(folder.folder_id); setDraft(''); }} className="p-0.5 hover:text-[var(--c-gold)]" data-testid={`file-folder-add-child-${folder.folder_id}`}><FolderPlus className="w-3 h-3" /></button>
              <button type="button" title="Renommer" onClick={(e) => { e.stopPropagation(); setEditing(folder.folder_id); setDraft(folder.name); }} className="p-0.5 hover:text-[var(--c-gold)]" data-testid={`file-folder-rename-${folder.folder_id}`}><Pencil className="w-3 h-3" /></button>
              <button type="button" title="Supprimer (les fichiers restent)" onClick={(e) => { e.stopPropagation(); onDelete(folder); }} className="p-0.5 hover:text-red-500" data-testid={`file-folder-delete-${folder.folder_id}`}><X className="w-3 h-3" /></button>
            </span>
          )}
          <span className={`text-[10.5px] tabular-nums ${folder ? 'group-hover:hidden' : ''} text-[var(--c-text-faint)]`}>{count}</span>
        </>
      )}
    </div>
  );

  const CreateInput = ({ depth }) => (
    <div className="flex items-center gap-1.5 py-1 pr-2" style={{ paddingLeft: 10 + depth * 14 }}>
      <Folder className="w-[15px] h-[15px] text-[var(--c-text-muted)]" />
      <input autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') submitCreate(); if (e.key === 'Escape') { setCreatingIn(undefined); setDraft(''); } }} placeholder="Nom du dossier" data-testid="file-folder-create-input" className="flex-1 min-w-0 bg-transparent border-b border-[var(--c-gold)] outline-none text-[12.5px]" />
      <button type="button" onClick={submitCreate} className="text-[var(--c-gold)]" data-testid="file-folder-create-confirm"><Check className="w-3.5 h-3.5" /></button>
    </div>
  );

  const renderTree = (pid, depth) => children(pid).flatMap((f) => {
    const count = f.count + children(f.folder_id).reduce((a, c) => a + (c.count || 0), 0);
    return [(
      <Fragment key={f.folder_id}>
        {Item({ k: f.folder_id, icon: selected === f.folder_id ? FolderOpen : Folder, label: f.name, count, depth, folder: f })}
        {creatingIn === f.folder_id && CreateInput({ depth: depth + 1 })}
        {!collapsed[f.folder_id] && renderTree(f.folder_id, depth + 1)}
      </Fragment>
    )];
  });

  return (
    <aside className="w-[232px] shrink-0 rounded-xl border border-[var(--c-border)] bg-[var(--c-surface-0)] p-2 self-start space-y-0.5" data-testid="file-folders-panel">
      <p className="px-2.5 pt-1 pb-1 text-[10px] uppercase tracking-[0.18em] font-semibold text-[var(--c-text-faint)]">Dossiers</p>
      {Item({ k: 'root', icon: Files, label: 'Tous les fichiers', count: totalCount })}
      {Item({ k: 'refs', icon: ImageIcon, label: 'Photos & rendus 3D', count: refCount, droppable: false })}
      <div className="my-1 mx-2 h-px bg-[var(--c-border)]" />
      {renderTree(null, 0)}
      {creatingIn === null ? CreateInput({ depth: 0 }) : (
        <button type="button" onClick={() => { setCreatingIn(null); setDraft(''); }} data-testid="file-folder-create-btn" className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-[12.5px] text-[var(--c-text-muted)] hover:text-[var(--c-gold)] hover:bg-[var(--c-surface-2)]">
          <FolderPlus className="w-[15px] h-[15px]" />Nouveau dossier
        </button>
      )}
      <p className="px-2.5 pt-2 text-[10.5px] text-[var(--c-text-faint)] leading-snug">Glissez un fichier sur un dossier pour le ranger.</p>
    </aside>
  );
};
